import { ImageIcon, SkeletonLoading } from '@saleshandy/design-system';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Images } from '../../../../../shared/app-constants';
import { TemplateHeaderProps } from '../type';
import { accessibleOnClick } from '../../../../../shared/utils/accessible-on-click';
import Help from '../../../../../shared/svg/help';
import ArrowGreyRight from '../../../../../assets/images/arrow-grey-right.svg';
import {
  OverlayPopover,
  OverlayTooltip,
  Placement,
} from '../../../../../shared/design-system/components/overlay';
import {
  resetIsBulkProspectSelected,
  resetSingleProspectDetailsRequest,
  setAiColumnModalVisible,
} from '../../../prospect-slice';
import Button from '../../../../../shared/design-system/ui/button';
import { Cross } from '../../../../../shared/svg';
import DiscardUnsavedChangesModal from './discard-unsaved-changes-modal';

import { AiColEnrichmentModalSteps } from '../enums';
import AiCreditCapsule from './ai-credit-capsule/ai-credit-capsule';
import { getProspectAndEmailSentLimit } from '../../../../home/components/header/utils/helper';
import { formatNumber } from '../../../../sequence/components/sequence-single-content/utils/helper';
import TruncatedTextWithAutoTooltip from '../../../../../shared/components/truncated-text-with-auto-tooltip';
import { isAgencyUser } from '../../../../../shared/utils/user-details';

const TemplateHeader: React.FC<TemplateHeaderProps> = memo(
  ({
    title,
    onClose,
    handleMoveBack,
    isTitleRequestPending = false,
    currentStep,
    isDirty = false,
  }) => {
    const [templateTitle, setTemplateTitle] = useState(title);
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
    const [discardAction, setDiscardAction] = useState<'back' | 'close' | null>(
      null,
    );
    const dispatch = useDispatch();
    const { remainingAICreditsLimit } = getProspectAndEmailSentLimit();

    const remainingAiCreditsValue = useMemo(
      () => Math.ceil(remainingAICreditsLimit),
      [remainingAICreditsLimit],
    );

    useEffect(() => {
      setTemplateTitle(title);
    }, [title]);

    const openDiscardModal = (action: 'back' | 'close') => {
      setDiscardAction(action);
      setIsDiscardModalOpen(true);
    };
    const closeDiscardModal = () => {
      setIsDiscardModalOpen(false);
      setDiscardAction(null);
    };

    return (
      <div className="template-header-container">
        <div className="template-header">
          <Button
            {...accessibleOnClick(() => {
              // Check if user has unsaved changes in TEMPLATE_DETAIL step
              if (
                isDirty &&
                (currentStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL ||
                  currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN ||
                  currentStep === AiColEnrichmentModalSteps.CUSTOM_PROMPT)
              ) {
                openDiscardModal('back');
              } else {
                dispatch(resetSingleProspectDetailsRequest());
                // reset is Bulk prospect selected flag
                dispatch(resetIsBulkProspectSelected());
                setTemplateTitle('');
                handleMoveBack();
              }
            })}
            className="template-header-back-btn"
          >
            <ImageIcon
              src={ArrowGreyRight}
              alt="ai-icon"
              className="left-arrow"
              width={16}
              height={16}
            />
          </Button>
          <div className="ai-title">
            <ImageIcon
              src={Images?.NoResultGradient}
              alt="ai-icon"
              width={16}
              height={16}
            />
            {isTitleRequestPending ? (
              <SkeletonLoading width={100} height={20} />
            ) : (
              <div className="use-template">
                {currentStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL ? (
                  <OverlayTooltip text={templateTitle}>
                    <span className="use-template-title">{templateTitle}</span>
                  </OverlayTooltip>
                ) : (
                  <span className="current-step-title">{templateTitle}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="template-header-right">
          {[
            AiColEnrichmentModalSteps.EXISTING_COLUMN,
            AiColEnrichmentModalSteps.TEMPLATE_DETAIL,
          ].includes(currentStep) && (
            <OverlayTooltip
              text={`Your AI credits: ${remainingAiCreditsValue}\nClick to buy more credits.`}
              className={`${isAgencyUser() ? 'ai-credits-tooltip' : ''}`}
            >
              <AiCreditCapsule
                credits={formatNumber(remainingAiCreditsValue || 0)}
                showBorder
                showPerUnit={false}
                showApproximation={false}
                iconClassName={isAgencyUser() ? 'disabled' : ''}
                className={`template-header-credit-capsule ${
                  isAgencyUser() ? 'disabled' : ''
                }`}
                onClick={() => {
                  if (isAgencyUser()) {
                    return;
                  }
                  window.open(
                    `/billing/subscriptions/ai-credits?openPaymentPopup=true`,
                    '_blank',
                  );
                }}
              />
            </OverlayTooltip>
          )}
          <div className="help-logo-container">
            <OverlayPopover
              className="help-popover"
              content={
                <div className="help-popover-content">
                  <div className="help-popover-text">
                    <div className="title">
                      Utilise templates to enhance and personalise your
                      prospecting efforts.
                    </div>
                    <a
                      href="https://docs.saleshandy.com/en/articles/12290754-how-to-use-ai-prospect-enrichment-to-run-hyper-personalized-campaigns"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="learn-more"
                    >
                      Learn More
                    </a>
                  </div>
                  <div className="cross-icon-container">
                    <span {...accessibleOnClick(() => setIsPopoverOpen(false))}>
                      <Cross width={15} height={15} className="cross-icon" />
                    </span>
                  </div>
                </div>
              }
              placement={Placement.Bottom}
              show={isPopoverOpen}
              onToggle={setIsPopoverOpen}
              rootClose={true}
            >
              <Help
                className="help-logo"
                {...accessibleOnClick(() => setIsPopoverOpen(!isPopoverOpen))}
              />
            </OverlayPopover>
          </div>
          <div
            className="cross-icon-container"
            {...accessibleOnClick(() => {
              // Check if user has unsaved changes in TEMPLATE_DETAIL step
              if (
                currentStep === AiColEnrichmentModalSteps.TEMPLATE_DETAIL ||
                currentStep === AiColEnrichmentModalSteps.EXISTING_COLUMN ||
                currentStep === AiColEnrichmentModalSteps.CUSTOM_PROMPT
              ) {
                if (isDirty) {
                  openDiscardModal('close');
                } else {
                  dispatch(resetIsBulkProspectSelected());
                  dispatch(setAiColumnModalVisible(false));
                  onClose();
                }
              } else {
                dispatch(resetIsBulkProspectSelected());
                dispatch(setAiColumnModalVisible(false));
                onClose();
              }
            })}
          >
            <Cross width={16} height={16} className="cross-icon-header" />
          </div>
          <DiscardUnsavedChangesModal
            show={isDiscardModalOpen}
            onDiscard={() => {
              if (discardAction === 'back') {
                dispatch(resetSingleProspectDetailsRequest());
                // reset is Bulk prospect selected flag
                dispatch(resetIsBulkProspectSelected());
                setTemplateTitle('');
                handleMoveBack();
              } else if (discardAction === 'close') {
                dispatch(resetIsBulkProspectSelected());
                dispatch(setAiColumnModalVisible(false));
                onClose();
              }
            }}
            onHide={closeDiscardModal}
            text={
              discardAction === 'back'
                ? 'Are you sure you want to go back without saving? Any unsaved changes will be lost.'
                : 'Are you sure you want to close without saving? Any unsaved changes will be lost.'
            }
          />
        </div>
      </div>
    );
  },
);

export default TemplateHeader;
